import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewEncapsulation, signal, computed } from '@angular/core';
import {
  IInternationalStudentPage,
  IPolicyDocumentYearChange,
  IStrapiDocument,
  IStrapiDocuments,
  YourInsurance,
} from 'src/app/interfaces/strapi';
import { PageComponent } from 'src/app/shared/components/page/page.component';
import { Meta, Title } from '@angular/platform-browser';
import { ResidenceService } from 'src/app/services/residence.service';
import { Residence } from 'src/app/shared/models/residence.model';
import { ApiSearchResponse } from 'src/app/interfaces/api';
import StrapiService from 'src/app/services/strapi.service';
import { getPolicyChangeYear } from 'src/app/shared/utils/date-utils';
import { NgIf, NgClass, NgStyle, NgFor, CurrencyPipe, DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { BreadcrumbsComponent } from '../../../shared/components/breadcrumbs/breadcrumbs.component';
import { AuthService } from '../../../services/auth.service';
import { RenderMarkdownComponent } from '../../../shared/components/render-markdown/render-markdown.component';

@Component({
  selector: 'app-your-insurance',
  templateUrl: './your-insurance.component.html',
  styleUrls: ['./your-insurance.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BreadcrumbsComponent,
    NgIf,
    RouterLink,
    NgClass,
    RenderMarkdownComponent,
    NgStyle,
    NgFor,
    CurrencyPipe,
    DatePipe,
  ],
})
export class YourInsuranceComponent extends PageComponent implements OnInit {
  public override pageData: YourInsurance = <YourInsurance>{};

  public internationalStudentData = signal<IInternationalStudentPage>(<IInternationalStudentPage>{});

  public updatedAccommodation = signal<Residence>(<Residence>{});

  public isLoading = signal<boolean>(false);

  public certificateUrl = signal<string | null>(null);

  public keyWordingUrl = signal<string | null>(null);

  public policyUrl = signal<string | null>(null);

  public policyYearChangeMessage = signal<string>('');

  public policyChangeYear = signal<string>('');

  public showSitesCoveredButton = signal<boolean>(false);

  public showPolicyChange = signal<boolean>(false);

  public showNoContentsCards = computed<boolean>(() => {
    if (this.auth.currentUser?.university.is_unihome_product) {
      return false;
    }

    if (
      !this.auth.currentUser?.accommodation?.covered &&
      !this.auth.currentUser?.coveredError
    ) {
      return true;
    }

    return false;
  });

  constructor(
    public override readonly titleService: Title,
    public override readonly metaService: Meta,
    public override readonly strapi: StrapiService,
    public readonly auth: AuthService,
    public readonly residenceService: ResidenceService,
    private readonly cdr: ChangeDetectorRef
  ) {
    super(titleService, metaService, strapi);
  }

  ngOnInit(): void {
    this.isLoading.set(true);

    this.strapi
      .getMyInsurance()
      .subscribe((data: IStrapiDocuments<YourInsurance>) => {
        this.parsePageData(data.data[0].attributes);
        super.setMetas();
      });

    this.strapi
      .getPolicyDocumentYearChange()
      .subscribe((response: IStrapiDocument<IPolicyDocumentYearChange>) => {
        this.showPolicyChange.set(!!response.data.attributes.message);
        this.policyYearChangeMessage.set(`<h4>${response.data.attributes.message}<h4>`);
        const currentYear = getPolicyChangeYear();
        this.policyChangeYear.set(`<span>September ${currentYear} </span>`);
      });

    if (this.auth.currentUser?.university.is_pathway_provider) {
      this.strapi
        .getInternationalStudentPage()
        .subscribe((data: IStrapiDocument<IInternationalStudentPage>) => {
          const studentData = data.data.attributes;
          if (this.auth.currentUser?.university.name) {
            studentData.dashboard_banner_content =
              studentData.dashboard_banner_content?.replaceAll(
                '<name>',
                this.auth.currentUser?.university.name
              ) ?? '';
          }
          this.internationalStudentData.set(studentData);
        });
    }

    this.residenceService
      .getAccommodationByUUID(this.auth.currentUser?.accommodation?.uuid ?? '')
      .pipe()
      .subscribe({
        next: (data: Partial<ApiSearchResponse>) => {
          const residence = <Residence>data.data;
          this.updatedAccommodation.set(residence);
          this.showSitesCoveredButton.set(!!residence.sites_covered_url);
        },
        complete: () => {
          this.getPolicyDocument('CERTIFICATE');
          this.getPolicyDocument('KEY WORDING');
          this.getPolicyDocument('POLICY WORDING');
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  protected override parsePageData(page: YourInsurance): void {
    super.parsePageData(page);
    this.pageData = page;
  }

  public getPolicyDocument(type: string): void {
    const docs = this.updatedAccommodation().policyDocuments;

    if (docs?.length) {
      const returnDoc = docs.find(
        (document) => document.type.toUpperCase() === type
      );
      if (returnDoc) {
        switch (type) {
          case 'CERTIFICATE':
            this.certificateUrl.set(`${returnDoc.file_url}?timestamp=${Date.now()}`);
            break;
          case 'KEY WORDING':
            this.keyWordingUrl.set(`${returnDoc.file_url}?timestamp=${Date.now()}`);
            break;
          case 'POLICY WORDING':
            this.policyUrl.set(`${returnDoc.file_url}?timestamp=${Date.now()}`);
            break;

          default:
            break;
        }
      }
    }
  }
}
